import { useCallback, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Crown, Sparkles, Check, ArrowRight, CreditCard, RotateCcw } from "lucide-react";
import i18n from "../i18n";
import {
  isNativeBilling,
  getCurrentOffering,
  packageForCycle,
  purchasePackage,
  restorePurchases,
  openStoreSubscriptions,
  storeName,
} from "../lib/billing";
import type { PurchasesOffering } from "@revenuecat/purchases-capacitor";

type Cycle = "monthly" | "yearly";

/* ===== i18n mini-hook (pattern déjà utilisé) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  // stable par langue : sinon `t` change à chaque rendu et relance les useEffect en boucle
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lng]
  );
  return { t, lng };
}

export const Upgrade = () => {
  const { t, lng } = useI18nSafe();
  const navigate = useNavigate();
  const native = isNativeBilling();
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [loading, setLoading] = useState(false);

  // --- Achat natif (RevenueCat) : offering du store ---
  const [offering, setOffering] = useState<PurchasesOffering | null>(null);
  const [offeringError, setOfferingError] = useState<string | null>(null);

  useEffect(() => {
    if (!native) return;
    let cancelled = false;
    (async () => {
      try {
        const o = await getCurrentOffering();
        if (!cancelled) {
          setOffering(o);
          if (!o) setOfferingError(t("upgrade:alerts.checkout_unavailable"));
        }
      } catch (e: any) {
        if (!cancelled) setOfferingError(e?.message || "offering error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [native, t]);

  // Prix (valeurs en EUR) — utilisées sur le web uniquement
  const PRICE_MONTHLY = 6.99;
  const PRICE_YEARLY = 69.0;

  const nf = new Intl.NumberFormat(lng === "fr" ? "fr-FR" : "en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2
  });

  const nativePkg = native ? packageForCycle(offering, cycle) : null;

  const displayPrice = native
    ? nativePkg
      ? `${nativePkg.product.priceString} ${
          cycle === "monthly" ? t("upgrade:per.month") : t("upgrade:per.year")
        }`
      : "—"
    : cycle === "monthly"
      ? `${nf.format(PRICE_MONTHLY)} ${t("upgrade:per.month")}`
      : `${nf.format(PRICE_YEARLY)} ${t("upgrade:per.year")}`;

  // --- Web : Stripe Checkout ---
  const startWebCheckout = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert(t("upgrade:alerts.must_login"));
      return;
    }

    const { data: session } = await supabase.auth.getSession();
    const token = session?.session?.access_token;

    const res = await fetch("/.netlify/functions/create-checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ plan: cycle, user_id: user.id }),
      credentials: "include",
    });

    const data = await res.json();
    if (data?.url) window.location.href = data.url;
    else alert(t("upgrade:alerts.checkout_unavailable"));
  };

  // --- Mobile : achat via le store ---
  const startNativePurchase = async () => {
    if (!nativePkg) {
      alert(t("upgrade:alerts.checkout_unavailable"));
      return;
    }
    const r = await purchasePackage(nativePkg);
    if (r.ok) {
      // le webhook RevenueCat met `is_premium` à jour côté serveur ; /success poll
      navigate("/success");
    } else if (!r.cancelled) {
      alert(r.message);
    }
  };

  const startCheckout = async () => {
    setLoading(true);
    try {
      if (native) await startNativePurchase();
      else await startWebCheckout();
    } finally {
      setLoading(false);
    }
  };

  const onRestore = async () => {
    setLoading(true);
    try {
      const r = await restorePurchases();
      if (r.ok && r.active) navigate("/success");
      else if (r.ok) alert(t("upgrade:alerts.restore_none", { defaultValue: "Aucun achat à restaurer." }));
      else alert(r.message);
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    if (native) {
      openStoreSubscriptions();
      return;
    }
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      const res = await fetch("/.netlify/functions/create-portal-session", {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const text = await res.text();
      let data: any = {};
      try { data = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && data?.url) {
        window.location.href = data.url;
      } else {
        console.error("Portal error:", text);
        alert(t("upgrade:alerts.portal_unavailable"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Liste des features localisées
  const features = [
    t("upgrade:features.advanced_stats"),
    t("upgrade:features.resources"),
    t("upgrade:features.multiteam"),
    t("upgrade:features.plays"),
    t("upgrade:features.heatmap"),
  ];

  return (
    <section className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="mx-auto max-w-5xl px-4 py-14">
        <div className="mx-auto max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-8 shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Crown className="h-5 w-5 text-orange-600 dark:text-orange-300" />
            {t("upgrade:badge")}
          </div>
          <h1 className="mt-2 text-center font-[Rubik] text-3xl font-bold italic text-orange-600 dark:text-orange-400 sm:text-4xl">
            {t("upgrade:title")}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-center text-gray-700 dark:text-gray-300">
            {t("upgrade:subtitle")}
          </p>

          {/* Toggle */}
          <div className="mt-6 flex justify-center">
            <div className="inline-flex rounded-xl border border-gray-200 bg-white/80 p-1 shadow-sm dark:border-white/10 dark:bg-white/5">
              <button
                className={`rounded-lg px-4 py-2 ${cycle === "monthly" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-300"}`}
                onClick={() => setCycle("monthly")}
              >
                {t("upgrade:cycle.monthly")}
              </button>
              <button
                className={`rounded-lg px-4 py-2 ${cycle === "yearly" ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-300"}`}
                onClick={() => setCycle("yearly")}
              >
                {t("upgrade:cycle.yearly")}
              </button>
            </div>
          </div>

          {/* Card */}
          <div className="mx-auto mt-8 max-w-lg rounded-2xl border border-orange-300/50 bg-white/80 p-6 shadow-lg ring-1 ring-orange-500/10 dark:border-orange-300/20 dark:bg-white/5">
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <span className="text-sm text-gray-500 dark:text-gray-400">{t("upgrade:plan.name")}</span>
            </div>
            <h3 className="text-2xl font-semibold">{t("upgrade:plan.headline")}</h3>

            {/* 💰 prix dynamique */}
            <p className="mt-2 text-xl font-bold text-orange-600 dark:text-orange-400">{displayPrice}</p>

            <ul className="mt-4 space-y-2 text-sm">
              {features.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" /> {f}
                </li>
              ))}
            </ul>

            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                onClick={startCheckout}
                disabled={loading || (native && !nativePkg)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 font-semibold text-white shadow-sm ring-1 ring-orange-700/20 transition-transform hover:-translate-y-0.5 hover:bg-orange-700 disabled:opacity-60"
              >
                <CreditCard className="h-4 w-4" />
                {t("upgrade:cta.pay", { cycle: t(cycle === "monthly" ? "upgrade:cycle.monthly_lc" : "upgrade:cycle.yearly_lc") })}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={openPortal}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-4 py-2.5 font-semibold text-gray-900 shadow-sm hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100 disabled:opacity-60"
              >
                {native ? t("upgrade:cta.manage_store", { defaultValue: "Gérer dans le store" }) : t("upgrade:cta.manage")}
              </button>
            </div>

            {native && (
              <button
                onClick={onRestore}
                disabled={loading}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-white/5 disabled:opacity-60"
              >
                <RotateCcw className="h-4 w-4" />
                {t("upgrade:cta.restore", { defaultValue: "Restaurer mes achats" })}
              </button>
            )}

            {native && offeringError && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">{offeringError}</p>
            )}

            {native ? (
              <p className="mt-3 text-xs text-gray-500">
                {t("upgrade:store_note", {
                  defaultValue:
                    "Abonnement à renouvellement automatique. Le paiement est débité sur votre compte {{store}} à la confirmation. Il se renouvelle au même tarif sauf annulation au moins 24 h avant la fin de la période en cours, dans les réglages de votre compte {{store}}.",
                  store: storeName(),
                })}{" "}
                <Link to="/rules" className="underline">{t("upgrade:terms", { defaultValue: "Conditions" })}</Link>
                {" · "}
                <Link to="/legal" className="underline">{t("upgrade:privacy", { defaultValue: "Confidentialité" })}</Link>
              </p>
            ) : (
              <p className="mt-3 text-xs text-gray-500">
                {t("upgrade:stripe_note")}
              </p>
            )}
          </div>

          <div className="mt-6 text-center text-sm">
            <Link to="/pricing" className="text-orange-600 hover:underline">
              {t("upgrade:see_features")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Upgrade;
export {};
